import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import './FleetEditModal.css'
import { addEscapeKeyListener } from '../../utils/common-methods'
import API from '../../service/api'
import API_BOOK from '../../service/endpoints'
import { customToast } from '../../utils/toasts'
import { FaTrash, FaUndo } from 'react-icons/fa'

const PROTECTED_FIELDS = ['_id', 'rn', 'cn', '__v', 'createdAt', 'updatedAt']

const DATE_FIELDS = ['dts', 'rts', 'gdts', 'grts', 'ncttime', 'PunchInDate', 'PunchOutDate']

const BOOLEAN_FIELDS = ['ia', 'ig', 'hai', 'hbi', 'hci']

const NUMBER_FIELDS = ['od', 'soc', 'soh', 'dte', 'csp', 'vbv', 'dev', 'dbv', 'vbc', 'vbe', 'vbt', 'vct', 'vmt', 'gss', 'gsp', 'lt', 'lng', 'gdir', 'cs', 'ifc', 'cim']

const FIELD_LABELS = {
  rn: 'Registration No.',
  cn: 'Chassis No.',
  imei: 'IMEI',
  oem: 'OEM',
  mdl: 'Model',
  od: 'Odometer',
  soc: 'SOC (%)',
  soh: 'SOH (%)',
  dte: 'DTE',
  csp: 'CAN Speed',
  vbv: 'Battery Voltage',
  dev: 'Device Ext Voltage',
  dbv: 'Device Battery Voltage',
  vbc: 'Battery Current',
  vbe: 'Battery Capacity',
  vbt: 'Battery Temp',
  vct: 'Controller Temp',
  vmt: 'Motor Temp',
  gss: 'GSM Signal',
  hai: 'Harsh Acceleration',
  hbi: 'Harsh Braking',
  hci: 'Harsh Cornering',
  gp: 'Gear Position',
  ig: 'Ignition',
  vs: 'Vehicle Status',
  ia: 'Is Active',
  src: 'Source',
  gsp: 'GPS Speed',
  lt: 'Latitude',
  lng: 'Longitude',
  gdir: 'GPS Direction',
  dts: 'CAN Timestamp',
  rts: 'CAN Response Time',
  gdts: 'GPS Timestamp',
  grts: 'GPS Response Time',
  ncttime: 'NCT Time',
  nct: 'NCT Type',
  mv: 'Movement',
  cs: 'Charging Status',
  ifc: 'IFC',
  cc: 'Charger Current',
  cim: 'CIM',
  cp: 'Charger Power',
  cv: 'Charger Voltage',
  dm: 'Drive Mode',
  vmdl: 'Vehicle Model Label',
  EmpCode: 'Employee Code',
  DEName: 'DE Name',
  PunchInDate: 'Punch In',
  PunchOutDate: 'Punch Out',
  city: 'City'
}

const TmsCurrentFixModal = ({ vehicle, onClose }) => {
  const [tmsData, setTmsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({})
  const [markedForDelete, setMarkedForDelete] = useState({})

  useEffect(() => {
    if (vehicle?.registration_number) {
      fetchTmsCurrent()
    }
    // eslint-disable-next-line
  }, [vehicle])

  useEffect(() => {
    const removeListener = addEscapeKeyListener(() => {
      onClose()
    })
    return () => {
      removeListener()
    }
  }, [onClose])

  const fetchTmsCurrent = async () => {
    setLoading(true)
    try {
      const response = await API({
        url: API_BOOK.FLEET.TMS_CURRENT_GET.url.replace(':registrationNumber', vehicle.registration_number),
        method: API_BOOK.FLEET.TMS_CURRENT_GET.method
      })
      if (response?.data?.status === 200 && response?.data?.data) {
        const data = response.data.data
        setTmsData(data)
        const initialForm = {}
        Object.keys(data).forEach(key => {
          if (PROTECTED_FIELDS.includes(key)) return
          if (DATE_FIELDS.includes(key)) {
            initialForm[key] = data[key] ? formatDateForInput(data[key]) : ''
          } else if (BOOLEAN_FIELDS.includes(key)) {
            initialForm[key] = data[key] ?? false
          } else {
            initialForm[key] = data[key] ?? ''
          }
        })
        setFormData(initialForm)
        setMarkedForDelete({})
      } else {
        customToast({ message: 'TMS Current data not found', svgImageName: 'decline' })
      }
    } catch (error) {
      console.error('Fetch TMS Current error', error)
      customToast({ message: 'Failed to fetch TMS Current data', svgImageName: 'decline' })
    }
    setLoading(false)
  }

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
  }

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }))
  }

  const toggleDelete = (key) => {
    setMarkedForDelete(prev => {
      const updated = { ...prev }
      if (updated[key]) {
        delete updated[key]
      } else {
        updated[key] = true
      }
      return updated
    })
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const updateFields = {}
      const deleteFields = Object.keys(markedForDelete)

      Object.keys(formData).forEach(key => {
        if (markedForDelete[key]) return
        const originalVal = tmsData[key]
        let newVal = formData[key]

        if (DATE_FIELDS.includes(key)) {
          const origFormatted = originalVal ? formatDateForInput(originalVal) : ''
          if (newVal !== origFormatted && newVal !== '') {
            updateFields[key] = new Date(newVal).toISOString()
          }
        } else if (BOOLEAN_FIELDS.includes(key)) {
          if (newVal !== originalVal) {
            updateFields[key] = newVal
          }
        } else if (NUMBER_FIELDS.includes(key)) {
          const numVal = newVal === '' ? null : Number(newVal)
          if (numVal !== originalVal) {
            updateFields[key] = numVal
          }
        } else {
          if (newVal !== (originalVal ?? '')) {
            updateFields[key] = newVal === '' ? null : newVal
          }
        }
      })

      if (!Object.keys(updateFields).length && !deleteFields.length) {
        customToast({ message: 'No changes detected', svgImageName: 'decline' })
        setSaving(false)
        return
      }

      const payload = {
        registrationNumber: vehicle.registration_number,
        updateFields: Object.keys(updateFields).length ? updateFields : undefined,
        deleteFields: deleteFields.length ? deleteFields : undefined
      }

      const response = await API({
        url: API_BOOK.FLEET.TMS_CURRENT_FIX.url,
        method: API_BOOK.FLEET.TMS_CURRENT_FIX.method,
        data: payload
      })

      if (response?.data?.status === 200) {
        customToast({ message: 'TMS Current updated successfully' })
        fetchTmsCurrent()
      } else {
        customToast({ message: response?.data?.error || 'Update failed', svgImageName: 'decline' })
      }
    } catch (error) {
      console.error('TMS Current Fix error', error)
      customToast({ message: 'Failed to update TMS Current', svgImageName: 'decline' })
    }
    setSaving(false)
  }

  const renderField = (key) => {
    const label = FIELD_LABELS[key] || key
    const isProtected = PROTECTED_FIELDS.includes(key)
    const isDeleted = markedForDelete[key]
    const originalValue = tmsData[key]

    if (isProtected) {
      return (
        <div key={key} style={fieldRowStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Form.Label style={labelStyle}>{label} <span style={{ color: '#999', fontSize: '11px' }}>({key})</span></Form.Label>
          </div>
          <div style={{ color: '#555', fontSize: '13px', padding: '4px 8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            {key === '_id' ? String(originalValue) : String(originalValue ?? '-')}
          </div>
        </div>
      )
    }

    return (
      <div key={key} style={{ ...fieldRowStyle, opacity: isDeleted ? 0.4 : 1, position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Form.Label style={labelStyle}>
            {label} <span style={{ color: '#999', fontSize: '11px' }}>({key})</span>
          </Form.Label>
          <div style={{ display: 'flex', gap: '4px' }}>
            {isDeleted ? (
              <button
                type='button'
                onClick={() => toggleDelete(key)}
                title='Undo delete'
                style={undoBtnStyle}
              >
                <FaUndo size={10} />
              </button>
            ) : (
              <button
                type='button'
                onClick={() => toggleDelete(key)}
                title='Delete field'
                style={deleteBtnStyle}
              >
                <FaTrash size={10} />
              </button>
            )}
          </div>
        </div>
        <div><small className='text-muted'>Current: {DATE_FIELDS.includes(key) ? formatDateDisplay(originalValue) : String(originalValue ?? 'null')}</small></div>
        {!isDeleted && (
          <>
            {DATE_FIELDS.includes(key) ? (
              <Form.Control
                type='datetime-local'
                value={formData[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                size='sm'
                step='1'
              />
            ) : BOOLEAN_FIELDS.includes(key) ? (
              <Form.Select
                value={String(formData[key] ?? false)}
                onChange={(e) => handleChange(key, e.target.value === 'true')}
                size='sm'
              >
                <option value='true'>true</option>
                <option value='false'>false</option>
              </Form.Select>
            ) : NUMBER_FIELDS.includes(key) ? (
              <Form.Control
                type='number'
                value={formData[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                size='sm'
                step='any'
              />
            ) : (
              <Form.Control
                type='text'
                value={formData[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                size='sm'
              />
            )}
          </>
        )}
        {isDeleted && (
          <div style={{ color: '#dc3545', fontSize: '12px', fontWeight: 600 }}>
            MARKED FOR DELETE
          </div>
        )}
      </div>
    )
  }

  const allKeys = tmsData ? Object.keys(tmsData) : []
  const protectedKeys = allKeys.filter(k => PROTECTED_FIELDS.includes(k))
  const editableKeys = allKeys.filter(k => !PROTECTED_FIELDS.includes(k))
  const deleteCount = Object.keys(markedForDelete).length

  return (
    <Modal
      show={!!vehicle}
      onHide={onClose}
      id='tmsCurrentFixModal'
      centered
      size='lg'
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '16px', color: '#1F2A5D' }}>TMS Current Fix - {vehicle?.registration_number}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px', maxHeight: '70vh' }}>
        {loading ? (
          <div className='text-center' style={{ padding: '40px' }}>
            <Spinner animation='border' variant='primary' />
          </div>
        ) : tmsData ? (
          <>
            {tmsData.gdts && tmsData.dts && (new Date(tmsData.gdts) - new Date(tmsData.dts)) > 3600000 && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '8px 12px',
                marginBottom: '15px',
                fontSize: '13px'
              }}>
                <strong>Warning:</strong> CAN timestamp is{' '}
                {Math.round((new Date(tmsData.gdts) - new Date(tmsData.dts)) / 3600000)} hours behind GPS timestamp.
                CAN data stuck due to validation loop.
              </div>
            )}

            <div style={{ marginBottom: '10px', color: '#666', fontSize: '12px' }}>
              Protected (read-only)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '15px' }}>
              {protectedKeys.map(key => renderField(key))}
            </div>

            <hr />

            <div style={{ marginBottom: '10px', color: '#1F2A5D', fontSize: '13px', fontWeight: 600 }}>
              Editable Fields ({editableKeys.length})
              {deleteCount > 0 && (
                <span style={{ color: '#dc3545', marginLeft: '10px', fontWeight: 'normal' }}>
                  {deleteCount} field(s) marked for delete
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {editableKeys.map(key => renderField(key))}
            </div>
          </>
        ) : (
          <div className='text-center' style={{ padding: '40px', color: '#999' }}>
            No TMS Current data found for this vehicle.
          </div>
        )}
      </Modal.Body>
      {tmsData && (
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={onClose}
            style={{ fontSize: '14px' }}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={saving}
            style={{
              borderRadius: '4px',
              backgroundColor: '#22046B',
              fontWeight: 'normal',
              fontSize: '14px',
              border: 'none'
            }}
          >
            {saving ? <Spinner animation='border' size='sm' /> : 'Apply Fix'}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  )
}

const labelStyle = {
  fontWeight: 600,
  color: '#1F2A5D',
  fontSize: '13px',
  marginBottom: '2px'
}

const fieldRowStyle = {
  padding: '8px',
  borderRadius: '6px',
  border: '1px solid #e9ecef',
  backgroundColor: '#fafafa'
}

const deleteBtnStyle = {
  backgroundColor: 'transparent',
  border: '1px solid #dc3545',
  color: '#dc3545',
  borderRadius: 4,
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0
}

const undoBtnStyle = {
  backgroundColor: 'transparent',
  border: '1px solid #28a745',
  color: '#28a745',
  borderRadius: 4,
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0
}

export default TmsCurrentFixModal
